// ChatUI.jsx
import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearTimeline, fetchTimeline, appendRealtimeCall, markReadLocal, deleteMessageLocal
} from '../../actions/timeline.actions';
import {
  sendTextMessage, sendFileMessage, sendVoiceMessage,
  markMessagesAsRead, deleteMessage, markMessagesAsReadSocket
} from '../../actions/messageActions';

import Loader from '../../components/Loader';
import { socket } from './socket';
import ReadReceiptsModal from './ReadReceiptsModal';
import ChatMessages from './ChatMessages';
import InputChatUI from './InputChatUI';
import ReplyTo from './ReplyTo';
import HeaderChatUI from './HeaderChatUI';
import NowPlayingBar from '../../components/media/NowPlayingBar';

export default function ChatUI({ chat, onBack }) {
  const chatId = chat?._id;
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth);
  const userId = auth?.user?._id;

  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [modalReaders, setModalReaders] = useState({ open: false, readers: [] });

  const fileInputImgRef = useRef(null);
  const fileInputDocRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const tl = useSelector((s) => s.timeline.byChat[chatId] || { items: [], nextCursor: null, loading: false });
  const items = tl.items || [];
  const loading = tl.loading;
  const nextCursor = tl.nextCursor;

  const atBottomRef = useRef(true);
  const pendingScrollRef = useRef(false);
  const typingTimerRef = useRef(null);

  // ВАЖНО: автоскролл делаем строго после того, как React обновил DOM
  const shouldScrollAfterRenderRef = useRef(false);

  const measureBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return true;
    const near = el.scrollHeight - (el.scrollTop + el.clientHeight) < 40;
    atBottomRef.current = near;
    return near;
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
  }, []);

  const requestScrollAfterRender = useCallback(() => {
    shouldScrollAfterRenderRef.current = true;
  }, []);

  // Срабатывает ПОСЛЕ коммита DOM (это решает “скролл раньше сообщения”)
  useLayoutEffect(() => {
    if (!shouldScrollAfterRenderRef.current) return;
    shouldScrollAfterRenderRef.current = false;
    scrollToBottom();
  }, [items.length, scrollToBottom]);

  // INIT: грузим последнюю страницу
  useEffect(() => {
    if (!chatId) return;
    setTypingUsers([]);
    dispatch(clearTimeline(chatId));
    dispatch(fetchTimeline(chatId, { limit: 15, append: false }))
      .then(() => {
        // после первичной загрузки — вниз
        requestScrollAfterRender();
      });
  }, [chatId, dispatch, requestScrollAfterRender]);

  // SOCKETS
  useEffect(() => {
    if (!chatId) return;

    const handleNewMessage = (m) => {
      if (String(m.chatId) !== String(chatId)) return;

      const mine = String(m.sender?._id || m.sender) === String(userId);
      const shouldStick = atBottomRef.current || pendingScrollRef.current || mine;

      dispatch(appendRealtimeCall(chatId, {
        _id: String(m._id),
        type: 'message',
        createdAt: m.createdAt || new Date().toISOString(),
        sender: m.sender,
        text: m.text || '',
        fileUrl: m.fileUrl || null,
        fileName: m.fileName || null,
        fileType: m.fileType || null,
        fileMime: m.fileMime || null,
        readBy: m.readBy || [],
        voiceId: m.voiceId || null,
        mediaDurationSec: m.mediaDurationSec || null,
      }));

      if (shouldStick) requestScrollAfterRender();
      if (pendingScrollRef.current && mine) pendingScrollRef.current = false;
    };

    const handleDeleted = (p) => {
      const messageId = typeof p === 'object' ? p.messageId : p;
      if (messageId) dispatch(deleteMessageLocal(chatId, messageId));
    };

    const handleMessagesRead = ({ chatId: roomId, userId: who }) => {
      if (String(roomId) !== String(chatId)) return;
      dispatch(markMessagesAsReadSocket(roomId, who));
      dispatch(markReadLocal(chatId, who));
    };

    const handleTypingStart = ({ chatId: roomId, user } = {}) => {
      if (String(roomId) !== String(chatId)) return;

      const typingUserId = String(user?._id || user?.id || '');
      if (!typingUserId || typingUserId === String(userId || '')) return;

      setTypingUsers((prev) => (
        prev.some((item) => String(item?._id || item?.id || '') === typingUserId)
          ? prev
          : [...prev, user]
      ));
    };

    const handleTypingStop = ({ chatId: roomId, userId: typingUserId } = {}) => {
      if (String(roomId) !== String(chatId)) return;

      setTypingUsers((prev) => prev.filter(
        (item) => String(item?._id || item?.id || '') !== String(typingUserId || '')
      ));
    };

    const handleTimelineCall = (it) => {
      dispatch(appendRealtimeCall(chatId, it));
      if (atBottomRef.current) requestScrollAfterRender();
    };

    const joinChat = () => {
      socket.emit('joinChat', chatId);
    };

    socket.on('connect', joinChat);
    socket.on('newMessage', handleNewMessage);
    socket.on('messageDeleted', handleDeleted);
    socket.on('messagesRead', handleMessagesRead);
    socket.on('typing:start', handleTypingStart);
    socket.on('typing:stop', handleTypingStop);
    socket.on('timeline:call', handleTimelineCall);

    if (socket.connected) {
      joinChat();
    }

    return () => {
      if (socket.connected) {
        socket.emit('leaveChat', chatId);
      }
      socket.off('connect', joinChat);
      socket.off('newMessage', handleNewMessage);
      socket.off('messageDeleted', handleDeleted);
      socket.off('messagesRead', handleMessagesRead);
      socket.off('typing:start', handleTypingStart);
      socket.off('typing:stop', handleTypingStop);
      socket.off('timeline:call', handleTimelineCall);
    };
  }, [chatId, dispatch, userId, requestScrollAfterRender]);

  // Маркировка прочитанным
  useEffect(() => {
    if (!chatId || !userId || !items.length) return;

    const unreadFromOthers = items.some(it => {
      if (it.type !== 'message') return false;
      const sid = (it.sender?._id || it.sender);
      if (String(sid) === String(userId)) return false;
      const readBy = (it.readBy || []).map(u => String(u?._id || u));
      return !readBy.includes(String(userId));
    });

    if (unreadFromOthers) {
      dispatch(markMessagesAsRead(chatId, userId));
      dispatch(markReadLocal(chatId, userId));
    }
  }, [chatId, userId, items, dispatch]);

  // Догрузка вверх
  const [pulling, setPulling] = useState(false);

  const onScroll = () => {
    const el = containerRef.current;
    if (!el) return;

    measureBottom();

    if (pulling || !nextCursor) return;
    if (el.scrollTop <= 80) {
      const prevH = el.scrollHeight;
      const prevTop = el.scrollTop;
      setPulling(true);

      dispatch(fetchTimeline(chatId, { before: nextCursor, limit: 15, append: true }))
        .then(() => {
          const newH = el.scrollHeight;
          el.scrollTop = (newH - prevH) + prevTop;
          setPulling(false);
        })
        .catch(() => setPulling(false));
    }
  };

  const stopTyping = useCallback(() => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    if (chatId && userId && socket.connected) {
      socket.emit('typing:stop', { chatId, userId });
    }
  }, [chatId, userId]);

  const handleTypingChange = useCallback((value) => {
    if (!chatId || !userId || !socket.connected) return;

    if (!String(value || '').trim()) {
      stopTyping();
      return;
    }

    socket.emit('typing:start', {
      chatId,
      user: {
        _id: userId,
        firstName: auth?.user?.firstName || '',
        lastName: auth?.user?.lastName || '',
        email: auth?.user?.email || '',
      },
    });

    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      typingTimerRef.current = null;
      if (socket.connected) {
        socket.emit('typing:stop', { chatId, userId });
      }
    }, 1200);
  }, [auth?.user?.email, auth?.user?.firstName, auth?.user?.lastName, chatId, stopTyping, userId]);

  useEffect(() => () => {
    stopTyping();
  }, [stopTyping]);

  // отправка текста/файла
  const handleSend = (e) => {
    if (e) e.preventDefault();
    if (!chatId || (!text.trim() && !file)) return;

    const prefix = replyTo ? `↩️ ${replyTo.author}: ${replyTo.snippet}\n` : '';
    const finalText = prefix + (text || '');

    pendingScrollRef.current = true;
    atBottomRef.current = true;

    if (file) {
      dispatch(sendFileMessage(chatId, { sender: userId, text: finalText }, file));
    } else {
      dispatch(sendTextMessage(chatId, { sender: userId, text: finalText }));
    }

    setText('');
    setFile(null);
    setReplyTo(null);
    stopTyping();

    setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0);
  };

  const openReaders = (readBy) => setModalReaders({ open: true, readers: readBy || [] });
  const closeReaders = () => setModalReaders({ open: false, readers: [] });

  const onReply = (msg) => {
    const author = typeof msg.sender === 'object'
      ? `${msg.sender.firstName || ''} ${msg.sender.lastName || ''}`.trim() || 'Собеседник'
      : 'Собеседник';

    const txt = (msg.text || msg.fileName || '').trim();
    setReplyTo({ _id: msg._id, author, snippet: txt.slice(0, 140) });
  };

  const sendVoice = ({ blob, durationMs }) => {
    pendingScrollRef.current = true;
    atBottomRef.current = true;
    dispatch(sendVoiceMessage(chatId, { sender: userId, durationMs }, blob));
  };

  return (
    <div className="ChatUI">
      <HeaderChatUI onBack={onBack} chatId={chatId} userId={userId} chat={chat} />
      <NowPlayingBar />

      <div
        className="ChatUI__messages-container"
        ref={containerRef}
        onScroll={onScroll}
      >
        {pulling && (
          <div style={{ textAlign: 'center', padding: '6px 0', color: '#9ca3af', fontSize: 12 }}>
            загружаем историю…
          </div>
        )}

        {loading && items.length === 0 ? (
          <Loader />
        ) : (
          <ChatMessages
            messages={items}
            currentUserId={userId}
            onDeleteMessage={(id) => {
              dispatch(deleteMessage(id, chatId, 'user'));
              dispatch(deleteMessageLocal(chatId, id));
            }}
            onOpenReaders={openReaders}
            onReply={onReply}
            selectedId={replyTo?._id || null}
          />
        )}
      </div>

      {!!typingUsers.length && (
        <div
          className="ChatUI__typing"
          style={{ padding: '5px 14px', fontSize: 12, opacity: 0.72 }}
        >
          {typingUsers.map((item) => (
            [item?.firstName, item?.lastName].filter(Boolean).join(' ').trim() || item?.email || 'Пользователь'
          )).join(', ')} {typingUsers.length > 1 ? 'печатают…' : 'печатает…'}
        </div>
      )}

      <ReplyTo replyTo={replyTo} setReplyTo={setReplyTo} />

      <InputChatUI
        fileInputImgRef={fileInputImgRef}
        fileInputDocRef={fileInputDocRef}
        InputChatUI={() => { }}
        attachBtnRef={{ current: null }}
        handleSend={handleSend}
        inputRef={inputRef}
        setText={setText}
        setFile={setFile}
        userId={userId}
        chatId={chatId}
        text={text}
        file={file}
        onVoiceFinished={sendVoice}
        onTypingChange={handleTypingChange}
      />

      <ReadReceiptsModal open={modalReaders.open} onClose={closeReaders} readers={modalReaders.readers} />
    </div>
  );
}
